import {
  GameActivityCreatedEvent,
  GameActivityUpdatedEvent,
  GameUpdatedEvent,
  LineUpUpdatedEvent,
  PlateAppearanceCreatedEvent,
  PlateAppearanceUpdatedEvent,
} from "@aura-frontend/wgs-client";
import { useCallback, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { postStreamGrant } from "@/api/gameState";

import { Activity, GameFullResponse } from "../types/gameFull";
import {
  toActivity,
  toGame,
  toGameTeams,
  toPlateAppearance,
  withOvertakenPendingMerged,
  withPendingPlateAppearance,
} from "./gameSnapshot";

/**
 * Game service live updates over its WebSocket (/ws/games/{gameId}). The WIN
 * API doesn't proxy the socket: it signs a 120 s stream grant and returns the
 * URL to dial with the grant in it. The grant is only checked when the socket
 * opens, so every (re)connect fetches a fresh one.
 */

export type GameStreamEvent =
  | (GameUpdatedEvent & { type: "game-updated" })
  | (LineUpUpdatedEvent & { type: "line-up-updated" })
  | (PlateAppearanceCreatedEvent & { type: "plate-appearance-created" })
  | (PlateAppearanceUpdatedEvent & { type: "plate-appearance-updated" })
  | (GameActivityCreatedEvent & { type: "game-activity-created" })
  | (GameActivityUpdatedEvent & { type: "game-activity-updated" });

const STREAM_EVENT_TYPES = new Set<string>([
  "game-updated",
  "line-up-updated",
  "plate-appearance-created",
  "plate-appearance-updated",
  "game-activity-created",
  "game-activity-updated",
]);

const parseStreamEvent = (raw: string): GameStreamEvent | null => {
  try {
    const event = JSON.parse(raw) as { type?: string };
    return event.type && STREAM_EVENT_TYPES.has(event.type)
      ? (event as GameStreamEvent)
      : null;
  } catch {
    return null;
  }
};

/** Up to 30 s between attempts */
const reconnectInterval = (attempt: number) =>
  Math.min(1000 * 2 ** attempt, 30_000);

/**
 * A saved tag reaches the service through omnibus and the broker, so until it
 * lands the service still sends the pitch as untagged (e.g. as hit data
 * arrives). Those updates keep the tags applied here on save.
 */
const keepLocalTags = (received: Activity, existing?: Activity): Activity =>
  existing?.result && !received.result
    ? {
        ...received,
        result: existing.result,
        resultingState: existing.resultingState,
      }
    : received;

const applyEvent = (
  data: GameFullResponse,
  event: GameStreamEvent,
): GameFullResponse => {
  switch (event.type) {
    case "game-updated":
      // A game's sport is set when it's created, so the snapshot's stands
      return {
        ...data,
        game: { ...toGame(event.state), sport: data.game.sport },
      };

    case "line-up-updated":
      return {
        ...data,
        lineUp: event.lineUp,
        game: { ...data.game, teams: toGameTeams(event.teams) },
      };

    case "plate-appearance-created":
    case "plate-appearance-updated": {
      const pa = toPlateAppearance(event.plateAppearance);
      const existing = data.plateAppearances.find(
        (p) => p.plateAppearanceId === pa.plateAppearanceId,
      );
      if (!existing) {
        return { ...data, plateAppearances: [pa, ...data.plateAppearances] };
      }
      return {
        ...data,
        plateAppearances: data.plateAppearances.map((p) =>
          p === existing ? pa : p,
        ),
      };
    }

    case "game-activity-created":
    case "game-activity-updated": {
      const received = toActivity(event.activity);
      // No pitch telemetry yet (e.g. a tag seen before its pitch)
      if (!received) return data;

      const existing = data.activities.find(
        (a) => a.activityId === received.activityId,
      );
      const activity = keepLocalTags(received, existing);
      if (existing) {
        return {
          ...data,
          activities: data.activities.map((a) =>
            a === existing ? { ...activity, playerTag: existing.playerTag } : a,
          ),
        };
      }

      // Newest first by throw time
      const index = data.activities.findIndex(
        (a) => a.pitch.eventTime < activity.pitch.eventTime,
      );
      const activities = [...data.activities];
      activities.splice(index === -1 ? activities.length : index, 0, activity);
      return {
        ...data,
        activities,
        plateAppearances: data.plateAppearances.map((pa) =>
          pa.plateAppearanceId === activity.plateAppearanceId &&
          !pa.activityIds.includes(activity.activityId)
            ? { ...pa, activityIds: [...pa.activityIds, activity.activityId] }
            : pa,
        ),
      };
    }

    default:
      return data;
  }
};

/**
 * When an at bat ends on an earlier pitch (the tagger is a few pitches
 * behind), the service releases the untagged pitches thrown after it to the
 * next batter: the ended plate appearance's activityIds drop them, but the
 * pitches themselves aren't resent. Moves any pitch still labelled with an
 * ended plate appearance it is no longer listed in to the current at bat.
 */
const withReleasedPitches = (data: GameFullResponse): GameFullResponse => {
  const current = data.plateAppearances.find(
    (pa) => pa.plateAppearanceId === data.game.currentPlateAppearanceId,
  );
  // Until the next at bat is seated (e.g. the game update that clears the
  // ended one hasn't arrived) there's nowhere to move them yet
  if (!current || current.status === "ended") return data;

  const endedMembers = new Map(
    data.plateAppearances
      .filter((pa) => pa.status === "ended")
      .map((pa) => [pa.plateAppearanceId, new Set(pa.activityIds)]),
  );
  const released = data.activities.filter(
    (activity) =>
      endedMembers.get(activity.plateAppearanceId)?.has(activity.activityId) ===
      false,
  );
  if (released.length === 0) return data;

  const releasedIds = new Set(released.map((a) => a.activityId));
  // Thrown before anything already in the at bat; activities are newest first
  const movedIds = released
    .map((a) => a.activityId)
    .filter((id) => !current.activityIds.includes(id))
    .reverse();
  return {
    ...data,
    activities: data.activities.map((activity) =>
      releasedIds.has(activity.activityId)
        ? { ...activity, plateAppearanceId: current.plateAppearanceId }
        : activity,
    ),
    plateAppearances: data.plateAppearances.map((pa) =>
      pa === current
        ? { ...pa, activityIds: [...movedIds, ...pa.activityIds] }
        : pa,
    ),
  };
};

/**
 * Applies one stream event to the game data. Created and updated events are
 * treated alike (upsert by id): the service only sends the kind as a hint.
 */
export const applyStreamEvent = (
  data: GameFullResponse,
  event: GameStreamEvent,
): GameFullResponse =>
  withReleasedPitches(
    withOvertakenPendingMerged(
      withPendingPlateAppearance(applyEvent(data, event)),
    ),
  );

/**
 * Streams the game's live updates to `onEvent` while `gameId` is set.
 * `onReconnect` fires when the socket reopens after a drop, so the caller can
 * catch up on anything missed while it was down.
 */
export const useGameStream = (
  gameId: string | undefined,
  onEvent: (event: GameStreamEvent) => void,
  onReconnect?: () => void,
) => {
  // The game the socket last opened for, to tell a reconnect from a first open
  const openedGameId = useRef<string | undefined>(undefined);

  const getStreamUrl = useCallback(async () => {
    if (!gameId) throw new Error("no game to stream");
    const { url } = await postStreamGrant(gameId);
    return url;
  }, [gameId]);

  const { readyState } = useWebSocket(
    getStreamUrl,
    {
      onOpen: () => {
        if (openedGameId.current === gameId) onReconnect?.();
        openedGameId.current = gameId;
      },
      onMessage: (message) => {
        const event = parseStreamEvent(message.data);
        if (event && event.gameId === gameId) onEvent(event);
      },
      retryOnError: true,
      shouldReconnect: () => true,
      reconnectAttempts: 50,
      reconnectInterval,
    },
    Boolean(gameId),
  );

  return { isConnected: readyState === ReadyState.OPEN, readyState };
};
